import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { ChevronDown, Search, Wrench } from "lucide-react";
import { useMemo, useState } from "react";
import {
  filterToolGroups,
  isNavigationItemActive,
  type MenuGroup,
} from "./dashboardNavigation";

type AdditionalToolsProps = {
  groups: MenuGroup[];
  badgeMap: Record<string, number>;
  location: string;
  onNavigate: (path: string) => void;
  isCompact: boolean;
  onRequestExpanded: () => void;
};

/**
 * A presentation-only overflow for infrequent tools. It receives the already
 * role- and feature-filtered menu model from DashboardLayout; navigation
 * visibility is not authorization.
 */
export default function AdditionalTools({
  groups,
  badgeMap,
  location,
  onNavigate,
  isCompact,
  onRequestExpanded,
}: AdditionalToolsProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filteredGroups = useMemo(() => filterToolGroups(groups, query), [groups, query]);

  if (groups.length === 0) return null;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && isCompact) onRequestExpanded();
    setOpen(nextOpen);
  };

  return (
    <div className="mt-1 shrink-0">
      <Collapsible open={open} onOpenChange={handleOpenChange}>
        <SidebarMenu className="px-2">
          <SidebarMenuItem>
            <SidebarMenuButton
              isActive={groups.some((group) => group.items.some((item) => isNavigationItemActive(item.path, location)))}
              onClick={() => handleOpenChange(!open)}
              tooltip="Additional Tools"
              className="h-9 font-normal"
              aria-label="Additional Tools"
            >
              <Wrench className="h-4 w-4" />
              <span className="flex-1">Additional Tools</span>
              {!isCompact && (
                <ChevronDown className={`h-3 w-3 text-sidebar-foreground/40 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        <CollapsibleContent className="overflow-hidden">
          <div className="px-3 pb-3 pt-2 group-data-[collapsible=icon]:hidden">
            <label htmlFor="additional-tools-search" className="sr-only">
              Search additional tools
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-sidebar-foreground/50" />
              <Input
                id="additional-tools-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search tools"
                className="h-8 border-sidebar-border bg-sidebar-accent/40 pl-8 text-xs text-sidebar-foreground placeholder:text-sidebar-foreground/50"
              />
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto pb-2">
            {filteredGroups.length > 0 ? (
              filteredGroups.map((group) => (
                <div key={group.key} className="mb-2">
                  <p className="px-4 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/40 group-data-[collapsible=icon]:hidden">
                    {group.label}
                  </p>
                  <SidebarMenu className="px-2">
                    {group.items.map((item) => {
                      const isActive = isNavigationItemActive(item.path, location);
                      return (
                        <SidebarMenuItem key={`${group.key}-${item.path}`}>
                          <SidebarMenuButton
                            isActive={isActive}
                            onClick={() => onNavigate(item.path)}
                            tooltip={item.label}
                            className="h-9 font-normal"
                          >
                            <item.icon className={`h-4 w-4 ${isActive ? "text-sidebar-primary" : ""}`} />
                            <span className="flex-1">{item.label}</span>
                            {badgeMap[item.path] ? (
                              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                                {badgeMap[item.path]}
                              </span>
                            ) : null}
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </div>
              ))
            ) : (
              <p className="px-4 py-2 text-xs text-sidebar-foreground/60 group-data-[collapsible=icon]:hidden">
                No matching tools.
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
